// Firestore Database Schema for VSLA SaaS System
// This file defines the TypeScript interfaces for all Firestore collections

export interface User {
  id: string;
  email: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
  role: 'member' | 'official' | 'admin' | 'donor';
  profilePicture?: string;
  dateOfBirth?: string;
  gender?: 'male' | 'female' | 'other';
  nationalId?: string;
  address?: Address;
  language: string;
  isActive: boolean;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Address {
  street?: string;
  city: string;
  county: string;
  country: string;
  postalCode?: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
}

export interface VSLA {
  id: string;
  name: string;
  description: string;
  location: Address;
  foundedDate: string;
  constitution: string;
  logo?: string;
  status: 'active' | 'inactive' | 'suspended';
  totalMembers: number;
  totalSavings: number;
  totalLoans: number;
  rules: VSLARules;
  currentCycle: string; // Reference to current cycle
  meetingSchedule: MeetingSchedule;
  bankAccount?: BankAccount;
  createdBy: string; // User ID
  createdAt: string;
  updatedAt: string;
}

export interface VSLARules {
  contributionRules: ContributionRules;
  loanRules: LoanRules;
  socialFundRules: SocialFundRules;
  meetingRules: MeetingRules;
  shareOutRules: ShareOutRules;
}

export interface ContributionRules {
  minimumContribution: number;
  maximumContribution: number;
  shareValue: number;
  currency: string;
  frequency: 'weekly' | 'monthly' | 'bi-weekly';
  latePaymentPenalty: number;
  isFlexible: boolean;
}

export interface LoanRules {
  maximumLoanAmount: number;
  minimumLoanAmount: number;
  interestRate: number;
  maximumLoanPeriod: number; // in weeks
  collateralRequired: boolean;
  guarantorsRequired: number;
  loanToSavingsRatio: number;
  processingFee: number;
}

export interface SocialFundRules {
  contributionPercentage: number;
  maximumAssistance: number;
  eligibilityPeriod: number; // months of membership
  assistanceTypes: string[];
}

export interface MeetingRules {
  quorum: number;
  attendanceRequirement: number;
  fineForAbsence: number;
  fineForLateness: number;
}

export interface ShareOutRules {
  cycleDuration: number; // in months
  distributionMethod: 'equal' | 'proportional';
  reserveFundPercentage: number;
}

export interface MeetingSchedule {
  dayOfWeek: number; // 0-6, Sunday is 0
  time: string; // HH:MM format
  venue: string;
  frequency: 'weekly' | 'bi-weekly' | 'monthly';
  nextMeeting: string;
}

export interface BankAccount {
  accountNumber: string;
  bankName: string;
  accountName: string;
  accountType: string;
}

export interface Membership {
  id: string;
  userId: string;
  vslaId: string;
  membershipNumber: string;
  joinDate: string;
  status: 'active' | 'inactive' | 'suspended';
  role: 'member' | 'chairperson' | 'secretary' | 'treasurer' | 'committee';
  totalSavings: number;
  totalLoans: number;
  totalContributions: number;
  sharesOwned: number;
  lastContribution?: string;
  performanceRating: number;
  createdAt: string;
  updatedAt: string;
}

export interface Cycle {
  id: string;
  vslaId: string;
  cycleNumber: number;
  startDate: string;
  endDate: string;
  status: 'active' | 'completed' | 'cancelled';
  totalSavings: number;
  totalLoans: number;
  totalInterest: number;
  totalShareOut: number;
  sharesIssued: number;
  shareValue: number;
  memberCount: number;
  meetingCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface Transaction {
  id: string;
  vslaId: string;
  userId: string;
  cycleId: string;
  type: 'contribution' | 'loan_disbursement' | 'loan_repayment' | 'social_fund' | 'penalty' | 'share_out';
  amount: number;
  description: string;
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  paymentMethod: 'cash' | 'mpesa' | 'bank_transfer';
  referenceNumber?: string;
  mpesaTransactionId?: string;
  meetingId?: string;
  approvedBy?: string;
  approvedAt?: string;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface Loan {
  id: string;
  vslaId: string;
  userId: string;
  cycleId: string;
  loanNumber: string;
  amount: number;
  interestRate: number;
  duration: number; // in weeks
  purpose: string;
  status: 'pending' | 'approved' | 'disbursed' | 'active' | 'completed' | 'defaulted' | 'cancelled';
  applicationDate: string;
  approvalDate?: string;
  disbursementDate?: string;
  dueDate: string;
  guarantors: string[]; // User IDs
  collateral?: string;
  totalRepayment: number;
  amountPaid: number;
  outstandingBalance: number;
  processingFee: number;
  penaltyAmount: number;
  repaymentSchedule: RepaymentSchedule[];
  approvedBy?: string;
  disbursedBy?: string;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface RepaymentSchedule {
  dueDate: string;
  amount: number;
  principal: number;
  interest: number;
  status: 'pending' | 'paid' | 'overdue';
  paidDate?: string;
  paidAmount?: number;
}

export interface Meeting {
  id: string;
  vslaId: string;
  cycleId: string;
  meetingNumber: number;
  scheduledDate: string;
  actualDate?: string;
  venue: string;
  agenda: string[];
  attendees: string[]; // User IDs
  absentees: string[]; // User IDs
  latecomers: string[]; // User IDs
  status: 'scheduled' | 'ongoing' | 'completed' | 'cancelled';
  minutes?: string;
  decisions: Decision[];
  contributionsCollected: number;
  loansApproved: number;
  chairperson: string; // User ID
  secretary: string; // User ID
  treasurer: string; // User ID
  documents: string[]; // Document URLs
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface Decision {
  id: string;
  description: string;
  type: 'loan_approval' | 'rule_change' | 'member_suspension' | 'project_approval' | 'other';
  votesFor: number;
  votesAgainst: number;
  abstentions: number;
  status: 'approved' | 'rejected';
  implementationDate?: string;
  implementedBy?: string;
}

export interface Project {
  id: string;
  vslaId: string;
  cycleId: string;
  name: string;
  description: string;
  category: string;
  proposedBy: string; // User ID
  proposalDate: string;
  requiredAmount: number;
  allocatedAmount: number;
  expectedReturns: number;
  duration: number; // in months
  status: 'proposed' | 'approved' | 'active' | 'completed' | 'cancelled';
  votes: Vote[];
  milestones: Milestone[];
  actualReturns: number;
  startDate?: string;
  endDate?: string;
  documents: string[]; // Document URLs
  createdAt: string;
  updatedAt: string;
}

export interface Vote {
  userId: string;
  vote: 'for' | 'against' | 'abstain';
  reason?: string;
  timestamp: string;
}

export interface Milestone {
  id: string;
  description: string;
  dueDate: string;
  status: 'pending' | 'completed' | 'delayed';
  completedDate?: string;
  completedBy?: string;
  notes?: string;
}

export interface SocialFund {
  id: string;
  vslaId: string;
  cycleId: string;
  balance: number;
  totalContributions: number;
  totalDisbursements: number;
  applications: SocialFundApplication[];
  createdAt: string;
  updatedAt: string;
}

export interface SocialFundApplication {
  id: string;
  userId: string;
  amount: number;
  purpose: string;
  urgency: 'low' | 'medium' | 'high' | 'emergency';
  status: 'pending' | 'approved' | 'disbursed' | 'rejected';
  applicationDate: string;
  approvalDate?: string;
  disbursementDate?: string;
  supportingDocuments: string[];
  approvedBy?: string;
  rejectionReason?: string;
}

export interface Notification {
  id: string;
  userId: string;
  vslaId?: string;
  type: 'meeting_reminder' | 'contribution_due' | 'loan_approved' | 'loan_overdue' | 'system_update' | 'general';
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  isRead: boolean;
  actionRequired: boolean;
  actionUrl?: string;
  scheduledFor?: string;
  sentAt?: string;
  channels: ('app' | 'sms' | 'email' | 'ussd')[];
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface SystemSettings {
  id: string;
  category: 'general' | 'mpesa' | 'sms' | 'ussd' | 'notifications' | 'security';
  key: string;
  value: any;
  description: string;
  isActive: boolean;
  updatedBy: string;
  updatedAt: string;
}

export interface AuditLog {
  id: string;
  userId: string;
  vslaId?: string;
  action: string;
  resource: string;
  resourceId: string;
  oldValue?: any;
  newValue?: any;
  ipAddress: string;
  userAgent: string;
  timestamp: string;
}

export interface Document {
  id: string;
  vslaId?: string;
  userId: string;
  name: string;
  type: 'constitution' | 'minutes' | 'financial_report' | 'loan_document' | 'identity' | 'other';
  fileUrl: string;
  fileSize: number;
  mimeType: string;
  description?: string;
  isPublic: boolean;
  tags: string[];
  uploadedAt: string;
  updatedAt: string;
}

// Collection names as constants
export const COLLECTIONS = {
  USERS: 'users',
  VSLAS: 'vslas',
  MEMBERSHIPS: 'memberships',
  CYCLES: 'cycles',
  TRANSACTIONS: 'transactions',
  LOANS: 'loans',
  MEETINGS: 'meetings',
  PROJECTS: 'projects',
  SOCIAL_FUNDS: 'social_funds',
  NOTIFICATIONS: 'notifications',
  SYSTEM_SETTINGS: 'system_settings',
  AUDIT_LOGS: 'audit_logs',
  DOCUMENTS: 'documents',
} as const;

// Firestore Security Rules Helper Types
export type UserRole = User['role'];
export type VSLAStatus = VSLA['status'];
export type TransactionType = Transaction['type'];
export type TransactionStatus = Transaction['status'];
export type LoanStatus = Loan['status'];
export type MeetingStatus = Meeting['status'];
export type ProjectStatus = Project['status'];
export type NotificationType = Notification['type'];
